/**
 * XML String to Object Parser for ESS Messages
 * Converts XML string MessageDetails to JavaScript object for proper XML generation
 */

/**
 * Parse XML string to JavaScript object
 * @param {string} xmlString - XML string like "<Tag1>value</Tag1><Tag2>value</Tag2>"
 * @returns {object} - JavaScript object like {Tag1: 'value', Tag2: 'value'}
 */
export const parseXMLString = (xmlString) => {
  if (!xmlString || typeof xmlString !== 'string') {
    return {};
  }

  const result = {};
  
  // Remove whitespace and newlines
  const cleanXml = xmlString.trim().replace(/\n\s*/g, '');
  
  // Match all XML tags and their content
  // Pattern: <TagName>content</TagName>
  // eslint-disable-next-line no-useless-escape
  const tagPattern = /<([^>\/\s]+)>([^<]*)<\/\1>|<([^>\/\s]+)>([^]*?)<\/\3>/g;
  let match;
  
  while ((match = tagPattern.exec(cleanXml)) !== null) {
    const tagName = match[1] || match[3];
    const content = match[2] || match[4];
    
    // Check if content contains nested XML
    if (content.includes('<')) {
      // Recursive parsing for nested structures
      result[tagName] = parseNestedXML(content);
    } else {
      result[tagName] = content;
    }
  }
  
  return result;
};

/**
 * Parse nested XML structures (like TermsCondition in PRODUCT_DETAIL)
 * @param {string} xmlString - XML string with nested elements
 * @returns {object|array} - Object or array of objects for repeated elements
 */
const parseNestedXML = (xmlString) => {
  const cleanXml = xmlString.trim();
  
  // Check if this contains multiple instances of the same tag (array case)
  const firstTagMatch = cleanXml.match(/<([^>\/\s]+)>/);
  if (!firstTagMatch) return cleanXml;
  
  const firstTag = firstTagMatch[1];
  // eslint-disable-next-line no-useless-escape
  const pattern = new RegExp(`<${firstTag}>(.*?)<\/${firstTag}>`, 'g');
  const matches = [...cleanXml.matchAll(pattern)];
  
  if (matches.length > 1) {
    // Multiple instances - return array
    return matches.map(match => {
      const innerContent = match[1];
      if (innerContent.includes('<')) {
        return parseXMLString(innerContent);
      }
      return innerContent;
    });
  }
  
  // Single nested structure - return object
  return parseXMLString(cleanXml);
};

/**
 * Convert template payload to proper format for backend
 * Parses MessageDetails string to object if needed
 * @param {object} payload - Payload from template (may have MessageDetails as string)
 * @returns {object} - Payload with MessageDetails as object
 */
export const preparePayloadForBackend = (payload) => {
  if (!payload) return payload;
  
  const prepared = { ...payload };
  
  // If MessageDetails is a string, parse it to object
  if (typeof prepared.MessageDetails === 'string') {
    prepared.MessageDetails = parseXMLString(prepared.MessageDetails);
  }
  
  return prepared;
};

/**
 * Example usage:
 * 
 * Input: 
 * '<ApplicationNumber>ESS123</ApplicationNumber><Reason>Success</Reason>'
 * 
 * Output:
 * {
 *   ApplicationNumber: 'ESS123',
 *   Reason: 'Success'
 * }
 * 
 * Nested Input:
 * '<ProductCode>LN001</ProductCode><TermsCondition><Number>TC001</Number></TermsCondition>'
 * 
 * Nested Output:
 * {
 *   ProductCode: 'LN001',
 *   TermsCondition: {
 *     Number: 'TC001'
 *   }
 * }
 */
